import { FC, ReactElement, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

import { DialogMenu, SelectBox } from "@/components";
import { filter_black_icon } from "@/constants";
import { useNavigate } from "react-router-dom";

export interface IFilter {
  onFiltersChange: (val: BasicFilters) => Promise<void>;
  onClose: () => void;
  anchorEl: Element | null;
  initialFilterState: BasicFilters;
  partnerData: SPOwnerPartnerResponse | undefined;
}

const FilterPopover: FC<IFilter> = ({
  onFiltersChange,
  onClose,
  anchorEl,
  initialFilterState,
  partnerData,
}): ReactElement => {
  const [filters, setFilters] = useState(initialFilterState);
  const [filterLoading, setFilterLoading] = useState(false);
  const navigate = useNavigate();

  const applyFilters = async (): Promise<void> => {
    setFilterLoading(true);
    try {
      await onFiltersChange(filters);
    } catch (error) {
    } finally {
      setFilterLoading(false);
      onClose();
    }
  };

  const resetFilters = (): void => {
    setFilters(initialFilterState);
    onFiltersChange(initialFilterState);
    navigate("/clients");
    onClose();
  };

  const handleChangeValue =
    (name: keyof BasicFilters) =>
    (value: string): void => {
      setFilters({
        ...filters,
        [name]: value,
      });
    };

  return (
    <DialogMenu
      id="advance-filter-menu"
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={onClose}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        p={2.5}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <img src={filter_black_icon} alt="filter" />
          <Typography
            variant="h5"
            fontSize={18}
            fontWeight={600}
            color="#030712"
          >
            Filter
          </Typography>
          <Box
            sx={{
              width: 24,
              height: 24,
              display: "grid",
              placeContent: "center",
              backgroundColor: "error.main",
              color: "white",
              borderRadius: "50%",
            }}
          >
            <Typography variant="caption" fontWeight={500} color="white">
              {Object.values(filters).filter(Boolean).length}
            </Typography>
          </Box>
        </Stack>

        <Typography
          sx={{
            color: "#116B6F",
            textDecorationLine: "underline",
            cursor: "pointer",
          }}
          variant="body2"
          fontWeight={500}
          onClick={resetFilters}
        >
          Clear All
        </Typography>
      </Stack>
      <Divider />
      <Stack sx={{ p: 2 }} spacing={1}>
        <SelectBox
          label="Partner"
          placeholder="Select Partner"
          value={filters.partner}
          handleChangeValue={handleChangeValue("partner")}
          options={
            partnerData?.data.map((partner) => ({
              label: partner.firstName + " " + partner.lastName,
              value: partner._id,
            })) || []
          }
        />
        <SelectBox
          label="Status"
          placeholder="Select Status"
          value={filters.status}
          handleChangeValue={handleChangeValue("status")}
          options={[
            { label: "Active", value: "active" },
            { label: "Restricted", value: "restricted" },
          ]}
        />
        {/* <DatePicker
          onChangeValue={handleChangeValue("dateAdded")}
          value={filters.dateAdded}
          label="Date Added"
        /> */}
      </Stack>
      <Divider />
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={1}
        p={2}
        sx={{
          background: "#F7F8F8",
        }}
      >
        <Button variant="white-outlined" sx={{ flex: 1 }} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ flex: 1, gap: 1 }}
          onClick={applyFilters}
          disabled={filterLoading}
          startIcon={filterLoading ? <CircularProgress size={20} /> : null}
        >
          {filterLoading ? "Applying" : "Apply"}
        </Button>
      </Stack>
    </DialogMenu>
  );
};

export default FilterPopover;
